import { inject, Injectable } from '@angular/core';
import { AppsyncClient, TreatmentApiClient, UserApiClient } from '../api-clients';
import { filter, first, Observable, of, shareReplay, switchMap, take } from 'rxjs';
import { Message, MessageUser } from '../models';
import getConversationMessages from '../../graphql/queries/getConversationMessages';
import { getConversationMessagesQuery, getUserQuery } from '../../graphql';
import subscribeToNewMessages from '../../graphql/queries/subscribeToNewMessages';
import { unshiftMessage } from '../utils';
import getUserById from '../../graphql/queries/getUserById';
import { DesignFilesAction, MessageType } from '../enums';
import { v4 as uuid } from 'uuid';
import { AuthStore } from '../stores';
import { User } from '@auth0/auth0-angular';
import createMessage from '../../graphql/mutations/createMessage';
import gql from 'graphql-tag';

const firstPageSize = 2000;

export interface StoredRedesignMessageData {
  actionMessageId: string;
  sourceMessageId: string;
  designFileAction: DesignFilesAction;
  revisionReasonId: number;
  skipTreatmentInfoMessage?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ChatMessageService {
  private readonly _appsync = inject(AppsyncClient);
  private readonly _authStore = inject(AuthStore);
  private readonly _treatmentClient = inject(TreatmentApiClient);
  private readonly _userClient = inject(UserApiClient);
  private readonly _messageAuthorProfiles: Map<string, ReturnType<UserApiClient['getOriginalUserProfile']>> = new Map();

  private getMessageAuthorProfile(userId: string): ReturnType<UserApiClient['getOriginalUserProfile']> {
    const cachedProfile = this._messageAuthorProfiles.get(userId);
    if (cachedProfile != null) {
      return cachedProfile;
    }

    const profile$ = this._userClient.getOriginalUserProfile(userId).pipe(take(1), shareReplay(1));
    this._messageAuthorProfiles.set(userId, profile$);
    return profile$;
  }

  getMessageUser(userId: string): Observable<MessageUser> {
    return new Observable<MessageUser>(sub => {
      this._appsync.client.then(async client => {
        const result = await client.query<getUserQuery>({
          query: getUserById,
          variables: { cognitoId: userId },
          fetchPolicy: 'network-only',
        });
        const { data } = result;
        if (!data || !data.getUser) {
          console.error(`appSync user ${userId} not found`);
          sub.next({
            cognitoId: userId,
            id: userId,
            registered: false,
            username: 'Unknown User',
          });
          sub.complete();
          return;
        }

        sub.next(data.getUser);
        sub.complete();
      });
    });
  }

  /** Retrieves message sender profiles in one authenticated API request. */
  public getMessageUsers(userIds: readonly string[]): Observable<MessageUser[]> {
    const uniqueUserIds: string[] = Array.from(new Set(userIds.filter((userId: string): boolean => userId.length > 0)));
    return uniqueUserIds.length === 0 ? of([]) : this._userClient.getMessageUserProfiles(uniqueUserIds);
  }

  observeMessages(conversationId: string): Observable<Message[]> {
    return new Observable(subscriber => {
      type RawMessage = Exclude<
        NonNullable<NonNullable<getConversationMessagesQuery['allMessageConnection']>['messages']>[number],
        null | undefined
      >;
      type NewMessageSubscriptionResult = {
        data?: Pick<getConversationMessagesQuery, 'subscribeToNewMessage'>;
      };

      const subscriptions: { unsubscribe: () => unknown }[] = [];
      const queryMessagesById = new Map<string, RawMessage>();
      const realtimeMessagesById = new Map<string, RawMessage>();
      let torndown = false;

      const emitMessages = (): void => {
        const messagesById = new Map<string, RawMessage>(queryMessagesById);
        realtimeMessagesById.forEach((message, id) => messagesById.set(id, message));

        const normalizedMessages = Array.from(messagesById.values())
          .sort((left, right) => (right.createdAt ?? right.id).localeCompare(left.createdAt ?? left.id))
          .map(message => ({
            ...message,
            createdAt: message.createdAt?.split('_')?.[0] ?? new Date().toISOString(),
            id: message.id.split('_')[1],
          }));

        subscriber.next(normalizedMessages);
      };

      const applyQueryMessages = (messages: readonly RawMessage[]): void => {
        const messageIds = new Set(messages.map(message => message.id));
        queryMessagesById.forEach((message, id) => {
          if (message.isSent === false && !messageIds.has(id)) queryMessagesById.delete(id);
        });
        messages.forEach(message => queryMessagesById.set(message.id, message));
        emitMessages();
      };

      const applyRealtimeMessage = (message: RawMessage): void => {
        realtimeMessagesById.set(message.id, message);
        emitMessages();
      };

      this._appsync.client.then(client => {
        const observable = client.watchQuery<getConversationMessagesQuery>({
          query: getConversationMessages,
          fetchPolicy: 'network-only',
          variables: {
            conversationId: conversationId,
            first: firstPageSize,
          },
        });

        subscriptions.push(
          observable.subscribe(({ data }) => {
            if (!data || !data.allMessageConnection) return;

            const queryMessages = data.allMessageConnection.messages;
            if (queryMessages == null) return;

            const newMessages = queryMessages.filter((m): m is Exclude<typeof m, null | undefined> => !!m);
            applyQueryMessages(newMessages);
          }),
          client
            .subscribe<NewMessageSubscriptionResult>({
              query: subscribeToNewMessages,
              variables: { conversationId },
            })
            .subscribe(
              ({ data }) => {
                const message = data?.subscribeToNewMessage;
                if (message != null) applyRealtimeMessage(message);
              },
              (error: unknown) => console.error('Failed to subscribe to new messages', error)
            )
        );

        if (torndown) subscriptions.forEach(subscription => subscription.unsubscribe());
      });

      return {
        unsubscribe: () => {
          torndown = true;
          subscriptions.forEach(s => s.unsubscribe());
          queryMessagesById.clear();
          realtimeMessagesById.clear();
        },
      };
    });
  }

  createNewMessage(
    messageType: MessageType,
    conversationId: string,
    msg: string,
    fileIds: number[],
    treatmentId: string,
    checkTreatmentStatus = true,
    authorOverride?: { userId: string; name: string; email?: string | null } | null
  ) {
    if (authorOverride != null) {
      const message = this.buildMessage(
        messageType,
        conversationId,
        msg,
        fileIds,
        authorOverride.userId,
        authorOverride.name,
        authorOverride.email ?? null
      );

      return this.persistMessage(messageType, conversationId, fileIds, treatmentId, checkTreatmentStatus, message);
    }

    const id = `${new Date().toISOString()}_${uuid()}`;
    return this._authStore.user$.pipe(
      filter((u): u is User => u != null),
      first(),
      switchMap(user =>
        this.getMessageAuthorProfile(user.sub ?? '').pipe(
          switchMap(profile => {
            const name = `${profile.personalInfo?.firstName ?? ''} ${profile.personalInfo?.lastName ?? ''}`.trim();
            const userId = user.sub;
            const message = this.buildMessage(
              messageType,
              conversationId,
              msg,
              fileIds,
              userId!,
              name,
              user.email ?? null,
              id
            );

            return this.persistMessage(
              messageType,
              conversationId,
              fileIds,
              treatmentId,
              checkTreatmentStatus,
              message
            );
          })
        )
      ),
      first()
    );
  }

  private buildMessage(
    messageType: MessageType,
    conversationId: string,
    msg: string,
    fileIds: number[],
    senderId: string,
    senderName: string,
    senderEmail: string | null,
    id: string = `${new Date().toISOString()}_${uuid()}`
  ): Message {
    return {
      conversationId: conversationId,
      content: msg,
      createdAt: id,
      sender: senderId,
      senderInfo: {
        name: senderName,
        email: senderEmail,
      },
      isSent: false,
      id: id,
      hasFiles: fileIds.length > 0,
      messageType:
        messageType === MessageType.Standard && fileIds.length > 0 ? MessageType.StandardWithFiles : messageType,
    };
  }

  private persistMessage(
    messageType: MessageType,
    conversationId: string,
    fileIds: number[],
    treatmentId: string,
    checkTreatmentStatus: boolean,
    message: Message
  ): Observable<string | null | undefined> {
    return new Observable<string | null | undefined>(observer => {
      this._appsync.client.then(client => {
        client
          .mutate({
            mutation: createMessage,
            variables: message,

            optimisticResponse: () => ({
              createMessage: {
                ...message,
                __typename: 'Message',
              },
            }),

            update: (proxy, { data: { createMessage: createdMessage } }: any) => {
              const options = {
                query: getConversationMessages,
                variables: { conversationId: conversationId, first: firstPageSize },
              };

              const data: any = proxy.readQuery(options);
              const nextData = unshiftMessage(data, createdMessage);
              proxy.writeQuery({ ...options, data: nextData });
            },
          })
          .then((result: any) => {
            const messageId = result.data.createMessage.id.split('_')[1];
            if (messageType !== MessageType.RequestRevision) {
              this._treatmentClient
                .setMessageId(treatmentId, {
                  messageId: messageId,
                  fileIds: fileIds,
                  checkTreatmentStatus: checkTreatmentStatus,
                })
                .pipe(first())
                .subscribe(() => {
                  observer.next(messageId);
                  observer.complete();
                });
            } else {
              observer.next(messageId);
              observer.complete();
            }
          })
          .catch(_ => {
            observer.next(null);
            observer.complete();
          });
      });
    });
  }

  storeMessageData(
    messageType: MessageType,
    conversationId: string,
    msg: string,
    fileIds: number[],
    treatmentId: string,
    sourceMessageId?: string,
    designFileAction?: DesignFilesAction,
    revisionReasonId?: number,
    skipTreatmentInfoMessage?: boolean
  ) {
    this._appsync.client.then(client => {
      client.writeQuery({
        query: gql`
          query GetStoredRedesignMessageData {
            messageType
            conversationId
            msg
            fileIds
            treatmentId
            sourceMessageId
            designFileAction
            revisionReasonId
            skipTreatmentInfoMessage
          }
        `,
        data: {
          messageType,
          conversationId,
          msg,
          fileIds,
          treatmentId,
          sourceMessageId,
          designFileAction,
          revisionReasonId,
          skipTreatmentInfoMessage,
        },
      });
    });
  }

  createMessageWithStoredRedesignData(checkTreatmentStatus = true) {
    return new Observable<StoredRedesignMessageData | null | undefined>(observer => {
      this._appsync.client.then(client => {
        try {
          const data: any = client.readQuery({
            query: gql`
              query GetStoredRedesignMessageData {
                messageType
                conversationId
                msg
                fileIds
                treatmentId
                sourceMessageId
                designFileAction
                revisionReasonId
                skipTreatmentInfoMessage
              }
            `,
          });

          if (!data) {
            observer.next(null);
            observer.complete();
            return;
          }

          const {
            messageType,
            conversationId,
            msg,
            fileIds,
            treatmentId,
            sourceMessageId,
            designFileAction,
            revisionReasonId,
            skipTreatmentInfoMessage,
          } = data;

          this.createNewMessage(messageType, conversationId, msg, fileIds, treatmentId, checkTreatmentStatus).subscribe(
            {
              next: result => {
                const mapped: StoredRedesignMessageData = {
                  actionMessageId: result ?? '',
                  sourceMessageId,
                  designFileAction,
                  revisionReasonId,
                  skipTreatmentInfoMessage,
                };
                observer.next(mapped);
                observer.complete();
              },
              error: () => {
                observer.next(null);
                observer.complete();
              },
            }
          );
        } catch (e) {
          observer.next(null);
          observer.complete();
        }
      });
    });
  }
}
